import { useState } from 'react';
import { Settings2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Select } from '@/components/ui/select';
import { useDataCache } from '@/store/data-cache-context';
import {
  DeviceMultiplier,
  DeviceUnitOfMeasure,
  updateDevice,
  updateDeviceConfig,
} from '../api/devices-api';
import { SALT_MULTIPLIER_OPTIONS, SALT_UOM_OPTIONS } from '../utils/salt-config';
import type { Device } from '@/types/api';

interface Props {
  device: Device;
  onSaved: () => void;
}

// Form body — just the fields + save button, no Card / Modal wrapper.
// Composed by SaltQuickConfigBanner (inline banner on Device Details)
// and SetupModePulseConfigModal (in-flow modal during Setup Mode) so the
// pulse-encoder config is collected identically from both surfaces.
export function SaltConfigForm({ device, onSaved }: Props) {
  const [multiplier, setMultiplier] = useState<DeviceMultiplier>(DeviceMultiplier.OneToOne);
  const [unitOfMeasure, setUnitOfMeasure] = useState<DeviceUnitOfMeasure>(DeviceUnitOfMeasure.Gallons);
  const [externalMeterModel, setExternalMeterModel] = useState(device.externalMeterModel ?? '');
  const [externalMeterSerial, setExternalMeterSerial] = useState(device.externalMeterSerial ?? '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { invalidate } = useDataCache();

  async function handleSave() {
    setSaving(true);
    setError(null);
    try {
      // Pulse config rides updateDeviceConfig (versioned history). The
      // connected-meter metadata lives on Device, so it goes through
      // updateDevice. Fire pulse-config first since it's the higher-
      // stakes value — if it fails we don't half-save the rest.
      await updateDeviceConfig(device.id, { multiplier, unitOfMeasure });
      await updateDevice(device.id, {
        externalMeterModel: externalMeterModel.trim() || undefined,
        externalMeterSerial: externalMeterSerial.trim() || undefined,
      });
      invalidate(['devices', 'meter-readings']);
      onSaved();
    } catch (err: unknown) {
      const message = (err as { response?: { data?: { message?: string } } })?.response?.data?.message
        ?? (err instanceof Error ? err.message : null);
      setError(message ?? 'Could not save pulse configuration. Try again.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="space-y-3">
      <div className="grid gap-3 sm:grid-cols-2">
        <Select
          label="Multiplier"
          value={String(multiplier)}
          onChange={(v) => setMultiplier(Number(v) as DeviceMultiplier)}
          options={SALT_MULTIPLIER_OPTIONS}
          disabled={saving}
        />
        <Select
          label="Unit of Measure"
          value={String(unitOfMeasure)}
          onChange={(v) => setUnitOfMeasure(Number(v) as DeviceUnitOfMeasure)}
          options={SALT_UOM_OPTIONS}
          disabled={saving}
        />
        <Input
          label="Connected meter model"
          placeholder="e.g., Versamag"
          value={externalMeterModel}
          onChange={(e) => setExternalMeterModel(e.target.value)}
          disabled={saving}
        />
        <Input
          label="Connected meter serial"
          placeholder="e.g., VM-12345"
          value={externalMeterSerial}
          onChange={(e) => setExternalMeterSerial(e.target.value)}
          disabled={saving}
        />
      </div>

      {error && <p className="text-xs text-error-600">{error}</p>}

      <div className="flex justify-end">
        <Button onClick={handleSave} isLoading={saving} disabled={saving}>
          Save calibration
        </Button>
      </div>
    </div>
  );
}

// Inline banner on the SALT device detail page when no pulse-encoder
// config has been saved yet. Wraps SaltConfigForm in the warning-yellow
// Card so it stands out below the device header.
//
// Setup Mode now also surfaces a Configure Pulses CTA (via
// SetupModePulseConfigModal) so the installer doesn't have to find this
// banner buried in Device Details.
export function SaltQuickConfigBanner({ device, onSaved }: Props) {
  return (
    <Card variant="elevated" className="border-warning-200 dark:border-warning-500/30 bg-warning-50/50 dark:bg-warning-500/5">
      <CardContent className="p-4 space-y-3">
        <div className="flex items-start gap-3">
          <div className="rounded-lg bg-warning-100 dark:bg-warning-500/20 text-warning-700 dark:text-warning-400 p-1.5">
            <Settings2 className="w-4 h-4" />
          </div>
          <div className="min-w-0">
            <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
              Configure pulse encoder
            </h3>
            <p className="mt-0.5 text-xs text-gray-600 dark:text-gray-400">
              This SALT transmitter doesn't have a multiplier or unit of measure
              set yet. Readings will appear as raw pulses until calibrated.
            </p>
          </div>
        </div>
        <SaltConfigForm device={device} onSaved={onSaved} />
      </CardContent>
    </Card>
  );
}
